<template>
  <div class="filter" :class="actionInputStyles">
    <div class="filter-inputs">
      <select
        v-model="action_name"
        class="action__question"
        :class="{ 'full-width': !showActionInput }"
        @change="resetAction()"
      >
        <option
          v-for="attribute in sortedActionTypes"
          :key="attribute.key"
          :value="attribute.key"
        >
          {{
            $t(`CONVERSATION.CARD_CONTEXT_MENU.${attribute.attributeI18nKey}`)
          }}
        </option>
      </select>

      <div
        v-if="showActionInput && inputType !== 'send_whatsapp_message'"
        class="filter__answer--wrap"
      >
        <div v-if="inputType">
          <div
            v-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="primarySelection"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="
                isOpportunityStatusAction ? statusOptions : dropdownValues
              "
              :allow-empty="false"
              :option-height="104"
            />
          </div>

          <div
            v-if="isLostStatusSelected"
            class="multiselect-wrap--small loss-reason-wrap"
          >
            <label class="loss-reason-label">
              {{ $t('CONVERSATION.CARD_CONTEXT_MENU.LOSS_REASON_LABEL') }}
            </label>
            <multiselect
              v-model="lossReasonSelection"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="lossReasonOptions"
              :allow-empty="false"
              :option-height="104"
            />
          </div>

          <div
            v-else-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="action_params"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              :multiple="true"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
              :option-height="104"
            />
          </div>

          <input
            v-else-if="inputType === 'email'"
            v-model="action_params"
            type="email"
            class="answer--text-input"
            :placeholder="$t('FORMS.MULTISELECT.ENTER_EMAIL')"
          />

          <input
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            class="answer--text-input"
            :placeholder="urlPlaceholder"
          />
          <input
            v-else-if="inputType === 'number'"
            v-model.number="action_params"
            type="number"
            step="0.01"
            class="answer--text-input"
            :placeholder="$t('FORMS.MULTISELECT.ENTER_VALUE')"
          />
          <automation-action-file-input
            v-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
            :accept="fileAcceptType"
          />
        </div>
      </div>
      <woot-button
        v-if="!isMacro"
        icon="dismiss"
        variant="clear"
        color-scheme="secondary"
        @click="removeAction"
      />
    </div>

    <div
      v-if="
        inputType === 'send_whatsapp_message' ||
        action_name === 'send_whatsapp_message_now'
      "
      class="send-whatsapp-message--full"
    >
      <div class="filter__answer--wrap">
        <div
          v-if="action_name === 'send_whatsapp_message'"
          class="schedule-hours-field mb-3"
        >
          <label class="block mb-1 font-semibold">
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.SCHEDULE_HOURS_LABEL') }}
          </label>
          <input
            type="number"
            min="1"
            max="8760"
            v-model.number="actionParams.scheduled_hours"
            class="answer--text-input"
            :placeholder="
              $t('CONVERSATION.CARD_CONTEXT_MENU.SCHEDULE_HOURS_PLACEHOLDER')
            "
            @input="onScheduledHoursInput"
            step="1"
          />
          <p v-if="errors.scheduledHours" class="filter-error">
            {{ errors.scheduledHours }}
          </p>
        </div>

        <div class="inbox-field mb-3">
          <label class="block mb-1 font-semibold">
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.INBOX_LABEL') }}
          </label>
          <select
            v-model="actionParams.inbox_id"
            class="answer--text-input mb-3 inbox-select"
            @change="onInboxChange"
          >
            <option disabled value="">
              {{ $t('CONVERSATION.CARD_CONTEXT_MENU.SELECT_INBOX') }}
            </option>
            <option
              v-for="inbox in inboxOptions"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>

          <p v-if="errors.inbox" class="filter-error mt-1">
            {{ errors.inbox }}
          </p>
        </div>

        <div v-if="actionParams.inbox_id">
          <label class="block mb-1 font-semibold mb-2">
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.TEMPLATE_LABEL') }}
          </label>
          <templates-picker
            :inbox-id="actionParams.inbox_id"
            :with-attachments="true"
            v-model="actionParams.template_id"
            :template-filter="{ authentication: false }"
            @onSelect="onTemplateSelect"
          />
          <p v-if="errors.template" class="filter-error mt-1">
            {{ errors.template }}
          </p>
        </div>

        <div
          v-if="actionParams.variables && actionParams.variables.length"
          class="template-variables mb-3 mt-3"
        >
          <label class="block mb-1 font-semibold">
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.TEMPLATE_VARIABLES_LABEL') }}
          </label>
          <div
            v-for="(variable, index) in actionParams.variables"
            :key="index"
            class="variable-row"
          >
            <woot-message-editor
              v-model="actionParams.variables[index]"
              rows="1"
              :enable-variables="true"
              :send-whatsapp-message="true"
              :placeholder="
                $t('CONVERSATION.CARD_CONTEXT_MENU.VARIABLE_PLACEHOLDER', {
                  index: index + 1,
                })
              "
              class="variable-editor mb-2"
              @input="validateVariables"
            />
          </div>
          <p v-if="errors.variables" class="filter-error mt-1">
            {{ errors.variables }}
          </p>
        </div>

        <div v-if="actionParams.template_body" class="template-body">
          <label>
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.TEMPLATE_BODY') }}
          </label>
          <p v-html="renderedTemplateBody"></p>
        </div>

        <div
          v-if="action_name === 'send_whatsapp_message'"
          class="cancel-toggle mt-4"
        >
          <label class="block mb-1 font-semibold">
            {{
              $t('CONVERSATION.CARD_CONTEXT_MENU.CANCEL_IF_INTERACTED_LABEL')
            }}
          </label>
          <label class="toggle-switch">
            <input
              type="checkbox"
              v-model="actionParams.cancel_if_interacted"
            />
            <span class="slider round"></span>
          </label>
        </div>
      </div>
    </div>

    <automation-action-team-message-input
      v-if="inputType === 'team_message'"
      v-model="action_params"
      :teams="dropdownValues"
      :action_name="action_name"
    />
    <automation-action-opportunity-activity-input
      v-if="inputType === 'opportunity_activity'"
      v-model="compositeParams"
      :activity-types="dropdownValues"
      :show-errors="isActionParamsDirty"
    />
    <automation-action-create-opportunity-input
      v-if="inputType === 'create_opportunity'"
      v-model="compositeParams"
      :pipelines="dropdownValues"
      :show-errors="isActionParamsDirty"
    />
    <woot-message-editor
      v-if="inputType === 'textarea'"
      v-model="castMessageVmodel"
      rows="4"
      :enable-variables="true"
      :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
      class="action-message"
    />
    <p
      v-if="
        inputType &&
          !isCompositeInput &&
          v.action_params.$dirty &&
          v.action_params.$error
      "
      class="filter-error mt-1"
    >
      {{ $t('CONVERSATION.CARD_CONTEXT_MENU.EMPTY_VALUE_ERROR') }}
    </p>
  </div>
</template>

<script>
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionOpportunityActivityInput from './AutomationActionOpportunityActivityInput.vue';
import AutomationActionCreateOpportunityInput from './AutomationActionCreateOpportunityInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import { compositeActionParams } from 'dashboard/helper/automationOpportunityActionHelper';

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionOpportunityActivityInput,
    AutomationActionCreateOpportunityInput,
    AutomationActionFileInput,
    WootMessageEditor,
    TemplatesPicker,
  },
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    v: {
      type: Object,
      default: () => null,
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedTemplate: null,
      errors: {
        scheduledHours: '',
        inbox: '',
        template: '',
        variables: '',
      },
      lastValidity: null,
    };
  },
  computed: {
    action_name: {
      get() {
        return this.value?.action_name || null;
      },
      set(value) {
        this.$emit('input', { ...this.value, action_name: value });
      },
    },
    action_params: {
      get() {
        return this.value?.action_params || {};
      },
      set(value) {
        this.$emit('input', { ...this.value, action_params: value });
      },
    },
    sortedActionTypes() {
      return [...this.actionTypes].sort((a, b) => {
        const labelA = this.$t(
          `CONVERSATION.CARD_CONTEXT_MENU.${a.attributeI18nKey}`
        );
        const labelB = this.$t(
          `CONVERSATION.CARD_CONTEXT_MENU.${b.attributeI18nKey}`
        );

        return labelA.localeCompare(labelB, 'pt-BR', {
          sensitivity: 'base',
        });
      });
    },
    inputType() {
      const action = this.actionTypes.find(a => a.key === this.action_name);
      return action ? action.inputType : null;
    },
    isOpportunityStatusAction() {
      return this.action_name === 'change_opportunity_status';
    },
    isCompositeInput() {
      return ['opportunity_activity', 'create_opportunity'].includes(
        this.inputType
      );
    },
    isActionParamsDirty() {
      return !!this.v?.action_params?.$dirty;
    },
    compositeParams: {
      get() {
        return compositeActionParams(this.action_params);
      },
      set(value) {
        this.action_params = value;
      },
    },
    actionParamsList() {
      const params = this.action_params;
      if (Array.isArray(params)) return params;
      return params && params.id !== undefined ? [params] : [];
    },
    statusOptions() {
      return this.dropdownValues.filter(
        option => option.__type !== 'loss_reason'
      );
    },
    lossReasonOptions() {
      return this.dropdownValues.filter(
        option => option.__type === 'loss_reason'
      );
    },
    primarySelection: {
      get() {
        if (!this.isOpportunityStatusAction) return this.action_params;
        return (
          this.actionParamsList.find(param => param.__type !== 'loss_reason') ||
          null
        );
      },
      set(value) {
        if (!this.isOpportunityStatusAction) {
          this.action_params = value;
          return;
        }
        const lossReason = this.actionParamsList.find(
          param => param.__type === 'loss_reason'
        );
        this.action_params =
          value?.id === 'lost' && lossReason
            ? [value, lossReason]
            : [value].filter(Boolean);
      },
    },
    lossReasonSelection: {
      get() {
        return (
          this.actionParamsList.find(param => param.__type === 'loss_reason') ||
          null
        );
      },
      set(value) {
        this.action_params = [this.primarySelection, value].filter(Boolean);
      },
    },
    isLostStatusSelected() {
      return (
        this.isOpportunityStatusAction && this.primarySelection?.id === 'lost'
      );
    },
    inboxOptions() {
      return this.dropdownValues.filter(i => i.__type === 'inbox');
    },
    selectedTemplateVariables() {
      const body =
        this.selectedTemplate?.components?.find(c => c.type === 'BODY')?.text ||
        '';
      return body.match(/{{\w+}}/g) || [];
    },
    selectedTemplateBody() {
      return (
        this.selectedTemplate?.components?.find(c => c.type === 'BODY')?.text ||
        ''
      );
    },
    actionInputStyles() {
      return {
        'has-error':
          this.inputType &&
          this.v.action_params?.$dirty &&
          this.v.action_params?.$error,
        'is-a-macro': this.isMacro,
      };
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          const value = this.action_params[0];
          return typeof value === 'string' ? value : '';
        }

        return typeof this.action_params === 'string' ? this.action_params : '';
      },
      set(value) {
        this.action_params = [value];
      },
    },
    isValid() {
      const noErrors =
        !this.errors.scheduledHours &&
        !this.errors.template &&
        !this.errors.variables &&
        !this.errors.inbox;
      const templateOk =
        !this.action_params.inbox_id || !!this.action_params.template_id;
      return noErrors && templateOk;
    },
    isFormValid() {
      const isSendNow = this.action_name === 'send_whatsapp_message_now';

      const hasScheduledHours =
        isSendNow ||
        (this.action_params.scheduled_hours && !this.errors.scheduledHours);

      const hasInbox = this.action_params.inbox_id && !this.errors.inbox;
      const hasTemplate =
        this.action_params.template_id && !this.errors.template;

      return hasScheduledHours && hasInbox && hasTemplate;
    },
    renderedTemplateBody() {
      let body = this.actionParams?.template_body || '';

      const vars = Array.isArray(this.actionParams.variables)
        ? this.actionParams.variables
        : [];

      if (!vars.length) return body;

      vars.forEach((value, index) => {
        const placeholder = new RegExp('\\{\\{' + (index + 1) + '\\}\\}', 'g');

        const replacement =
          typeof value === 'string' ? value : String(value || '');

        if (replacement && replacement.trim() !== '') {
          body = body.replace(placeholder, replacement);
        }
      });

      return body;
    },
    actionParams() {
      if (!this.action_params) {
        this.$emit('input', {
          ...this.value,
          action_params: [{}],
        });
        return {};
      }

      if (Array.isArray(this.action_params)) {
        if (!this.action_params[0]) {
          this.$set(this.action_params, 0, {});
        }
        return this.action_params[0];
      }

      return this.action_params;
    },
    urlPlaceholder() {
      return this.action_name === 'execute_builder_flow'
        ? this.$t('FORMS.MULTISELECT.ENTER_VALID_TOKEN')
        : this.$t('FORMS.MULTISELECT.ENTER_URL');
    },
    fileAcceptType() {
      if (this.action_name === 'update_contact_avatar') {
        return 'image/*';
      }

      return '*';
    },
  },
  watch: {
    'action_params.scheduled_hours': {
      handler() {
        this.validateScheduledHours();
        this.emitValidityIfChanged();
      },
      deep: false,
    },
    'action_params.inbox_id': {
      handler() {
        this.validateTemplate();
        this.emitValidityIfChanged();
      },
      deep: false,
    },
    'action_params.template_id': {
      handler() {
        this.validateTemplate();
        this.validateVariables();
        this.emitValidityIfChanged();
      },
      deep: false,
    },
    'action_params.variables': {
      handler() {
        this.validateVariables();
        this.emitValidityIfChanged();
      },
      deep: true,
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onInboxChange() {
      this.selectedTemplate = null;

      this.actionParams.template_id = '';
      this.actionParams.template_body = '';
      this.actionParams.variables = [];

      this.errors.template = '';
      this.validateTemplate();
      this.emitValidityIfChanged();
    },
    onTemplateSelect(template) {
      this.selectedTemplate = template;
      const bodyText =
        template.components.find(c => c.type === 'BODY')?.text || '';
      const matches = bodyText.match(/{{\w+}}/g) || [];
      this.action_params = {
        ...this.action_params,
        template_id: template.id,
        template_name: template.name,
        language: template.language,
        namespace: template.namespace,
        template_body: bodyText,
        variables: matches.map(() => ''),
        category: template.category || 'UTILITY',
      };
      this.validateTemplate();
      this.validateVariables();
      this.emitValidityIfChanged();
    },
    onScheduledHoursInput(e) {
      const val = this.action_params.scheduled_hours;
      if (val === '' || val === null || typeof val === 'undefined') {
        this.action_params = { ...this.action_params, scheduled_hours: '' };
        this.errors.scheduledHours = '';
        this.emitValidityIfChanged();
        return;
      }
      const intVal = parseInt(val, 10);
      if (Number.isNaN(intVal)) {
        this.errors.scheduledHours = this.$t(
          'CONVERSATION.CARD_CONTEXT_MENU.INVALID_HOUR'
        );
        this.action_params = { ...this.action_params, scheduled_hours: '' };
      } else {
        this.action_params = { ...this.action_params, scheduled_hours: intVal };
        this.validateScheduledHours();
      }
      this.emitValidityIfChanged();
    },
    validateScheduledHours() {
      const v = this.action_params.scheduled_hours;
      this.errors.scheduledHours = '';
      if (v === '' || v === null || typeof v === 'undefined') {
        this.errors.scheduledHours = '';
        return;
      }
      if (!/^\d+$/.test(String(v))) {
        this.errors.scheduledHours = this.$t(
          'CONVERSATION.CARD_CONTEXT_MENU.INVALID_HOUR'
        );
        return;
      }
      const intVal = Number(v);
      if (intVal < 1) {
        this.errors.scheduledHours = this.$t(
          'CONVERSATION.CARD_CONTEXT_MENU.MIN_HOUR'
        );
        this.action_params = { ...this.action_params, scheduled_hours: 1 };
        return;
      }
      if (intVal > 8760) {
        this.errors.scheduledHours = this.$t(
          'CONVERSATION.CARD_CONTEXT_MENU.MAX_HOUR'
        );
        this.action_params = { ...this.action_params, scheduled_hours: 8760 };
        return;
      }
      this.errors.scheduledHours = '';
    },
    validateTemplate() {
      this.errors.template = '';
      this.errors.inbox = '';
      if (this.action_params.inbox_id && !this.action_params.template_id) {
        this.errors.template = this.$t(
          'CONVERSATION.CARD_CONTEXT_MENU.SELECT_TEMPLATE'
        );
      }
    },
    validateVariables() {
      this.errors.variables = '';
      const vars = this.action_params.variables || [];
      if (vars.length > 0) {
        const emptyIndex = vars.findIndex(v => !String(v || '').trim());
        if (emptyIndex !== -1) {
          this.errors.variables = this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.FILL_VARIABLE',
            {
              index: emptyIndex + 1,
            }
          );
        }
      }
    },
    emitValidityIfChanged() {
      const current = this.isFormValid;
      if (this.lastValidity !== current) {
        this.lastValidity = current;
        this.$emit('validity-changed', current);
      }
    },
  },
  mounted() {
    this.validateScheduledHours();
    this.validateTemplate();
    this.validateVariables();
    this.emitValidityIfChanged();
  },
};
</script>

<style lang="scss" scoped>
.filter {
  background: var(--color-background);
  padding: var(--space-small);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  margin-bottom: var(--space-small);

  &.is-a-macro {
    margin-bottom: 0;
    background: var(--white);
    padding: var(--space-zero);
    border: unset;
    border-radius: unset;
  }
}

.filter.has-error {
  background: var(--r-50);
}

.filter-inputs {
  display: flex;
  flex-wrap: wrap;
}

.action__question {
  max-width: 50%;
  margin-right: var(--space-smaller);
}

.action__question.full-width {
  max-width: 100%;
}

.filter__answer--wrap {
  flex-grow: 1;
  max-width: 50%;
  margin-right: var(--space-smaller);

  input {
    margin-bottom: 0;
  }
}

.loss-reason-wrap {
  flex-basis: 100%;
  margin-top: var(--space-smaller);
}

.loss-reason-label {
  display: block;
  margin-bottom: var(--space-micro);
  font-weight: var(--font-weight-medium);
}

.send-whatsapp-message--full {
  width: 100%;
  flex-basis: 100%;
  margin-top: var(--space-small);

  .filter__answer--wrap {
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--space-small);
  }

  .schedule-hours-field,
  .inbox-field,
  select.answer--text-input,
  .template-preview,
  .template-variables,
  .cancel-toggle {
    width: 100% !important;
    max-width: 285px !important;
    margin-top: 0 !important;
  }

  .inbox-select,
  .schedule-hours-field input,
  select.answer--text-input {
    width: 100% !important;
    height: 38px !important;
    padding: 0 var(--space-smaller) !important;
    border: 1px solid var(--color-border) !important;
    border-radius: var(--border-radius-medium) !important;
    box-sizing: border-box !important;
    font-size: var(--font-size-small) !important;
    margin-top: var(--space-smaller) !important;
    margin-bottom: var(--space-smaller) !important;
  }

  .template-preview {
    background: var(--s-25);
    border-radius: var(--border-radius-medium);
    padding: var(--space-small);
    max-width: 285px !important;
  }

  .template-variables {
    display: flex;
    flex-direction: column;
    gap: var(--space-smaller);

    .variable-row input {
      width: 100% !important;
      height: 38px !important;
      border: 1px solid var(--color-border) !important;
      border-radius: var(--border-radius-medium) !important;
      padding: 0 var(--space-smaller) !important;
      box-sizing: border-box !important;
      font-size: var(--font-size-small) !important;
    }
  }

  .cancel-toggle {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }

  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 46px;
    height: 26px;
  }

  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 26px;
    width: 46px;
    background-color: #d1d5db;
    border-radius: 34px;
    cursor: pointer;
    transition: background-color 0.3s ease, box-shadow 0.3s ease;
    margin-top: var(--space-mini) !important;
  }

  .slider:before {
    content: '';
    position: absolute;
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: #fff;
    border-radius: 50%;
    transition: transform 0.3s ease;
  }

  input:checked + .slider {
    background-color: #1e3a8a;
    box-shadow: 0 0 0 2px #1e3a8a40;
  }

  input:checked + .slider:before {
    transform: translateX(20px);
  }
}

.schedule-hours-field label,
.cancel-toggle label {
  white-space: nowrap;
}

.filter-error {
  color: var(--danger, #b91c1c);
  font-size: 0.85rem;
  margin-top: var(--space-small);
  margin-bottom: var(--space-small);
}

.variable-editor {
  border: 1px solid var(--color-border) !important;
  border-radius: var(--border-radius-medium) !important;
  padding: 0 var(--space-smaller) !important;
  min-height: 38px !important;
  display: flex;
  align-items: center;
  font-size: var(--font-size-small);
  background: #fff;
}

.variable-editor :deep(.ProseMirror) {
  padding: 6px 0 !important;
  min-height: 26px !important;
  overflow: hidden;
}

.variable-editor :deep(.ProseMirror p) {
  margin: 0 !important;
}

::v-deep {
  .ProseMirror-menubar {
    display: none;
  }
}

.variable-editor::v-deep .ProseMirror-woot-style {
  min-height: auto !important;
}
</style>
